import { countRunes } from 'src/pages/MessengerV2/MessageSearch.functions';
import {
  tMessageSearchQueryKind,
  tMessageSearchSearchedProps,
  tMessageSearchSettledBy,
  tMessageSearchSurface,
} from './messageSearchAnalytics.constants';

/** A digit run this long, separators included, is read as a phone number. */
const PHONE_MIN_DIGITS = 7;

/** A shorter run is still masked from here up: order numbers, postal codes. */
const NUMBER_MIN_DIGITS = 4;

/** `query_masked` is cut here. The in-chat bar alone accepts 255 characters. */
export const MASKED_QUERY_MAX_RUNES = 100;

/**
 * How much of a query the mask reads. The inbox box has no length limit, and
 * the mask must cost the same whether someone typed a word or pasted a file;
 * nothing past this point could reach `query_masked` anyway. It holds for the
 * text as typed and again for the text once folded, which can be longer.
 */
export const MASK_INPUT_MAX_LENGTH = 1000;

const TOKEN = /\[(phone|email|url|number)\]/g;

/**
 * A link that says so: a scheme or `www.`, wherever it starts. Not anchored to
 * the start of the word, so `(https://…)` and `"www.…"` are links too.
 */
const LINK_WITH_SCHEME = /(?:https?:\/\/|www\.)\S+/gu;

/** A link that does not: a domain and a path, as in `instagram.com/tienda`. */
const DOMAIN_WITH_PATH =
  /[\p{L}\p{N}][\p{L}\p{N}-]*(?:\.[\p{L}\p{N}-]+)*\.\p{L}{2,}\/\S*/gu;

/** What closes a sentence, a quote or a bracket after a link: not part of it. */
const AFTER_A_LINK = /[)\]}>"'»”’.,;:!?]+$/u;

/**
 * Digits with what comes between them: `+52 (55) 1234-5678`. Any dash is a
 * dash (`\p{Pd}`: en, em, non-breaking), and so is what nobody sees
 * (`\p{Cf}`: the zero-width space some pages put in a number, the direction
 * marks chat apps write around one).
 */
const DIGIT_RUN = /\+?\(?\p{Nd}[\p{Nd}\p{Pd}\p{Cf}\s().]*\p{Nd}\)?/gu;

const LONG_NUMBER = new RegExp(`\\p{Nd}{${NUMBER_MIN_DIGITS},}`, 'gu');

const NOT_A_DIGIT = /\P{Nd}/gu;

const A_WORD_CHARACTER = /[\p{L}\p{N}]/u;

/**
 * The part of a text the mask reads: up to MASK_INPUT_MAX_LENGTH, cut at the
 * last whole word, so half a number is never left unmasked at the edge. A
 * single word longer than that has no whole word to cut at and is not read.
 */
function readablePart(text: string): string {
  return text.length > MASK_INPUT_MAX_LENGTH
    ? text.slice(0, MASK_INPUT_MAX_LENGTH).replace(/\S*$/, '')
    : text;
}

/** The token for a link, with what followed it left where it was. */
function maskLink(link: string): string {
  return `[url]${link.match(AFTER_A_LINK)?.[0] ?? ''}`;
}

/**
 * What a masked query is made of: a token's kind when it holds nothing but
 * tokens of that one kind, punctuation aside; `mixed` when it holds words too,
 * or tokens of several kinds; `text` when it holds no token.
 */
function kindOf(masked: string): tMessageSearchQueryKind {
  const kinds = new Set(
    Array.from(
      masked.matchAll(TOKEN),
      (token) => token[1] as tMessageSearchQueryKind,
    ),
  );

  if (!kinds.size) return 'text';

  const [onlyKind] = kinds;
  const hasWords = A_WORD_CHARACTER.test(masked.replace(TOKEN, ''));

  return kinds.size === 1 && !hasWords ? onlyKind : 'mixed';
}

/**
 * What a search query may say to analytics (ANA-1).
 *
 * The query is free text typed over a customer's conversations, so it is never
 * emitted as typed. What identifies a person by itself is replaced by a token:
 *
 * - any word with an `@`, a half-typed address too: `[email]`;
 * - any link, `[url]`: what starts with `http://`, `https://` or `www.`
 *   wherever it starts in its word, and a domain followed by a path
 *   (`instagram.com/tienda`). The brackets, quotes and punctuation around it
 *   stay where they were. A domain alone (`tienda.mx`) stays: it reads as a
 *   name, and nothing tells it from `informe.pdf` or from a full stop with no
 *   space after it;
 * - any run of 7 or more digits with the separators of a phone number, any
 *   dash and any invisible character among them: `[phone]`;
 * - what is left of 4 or more digits in a row: `[number]`. Numbers of up to 3
 *   digits stay ("talla 28").
 *
 * Full-width signs and digits are folded to the plain ones first (NFKC), and a
 * digit is a digit in any script, so neither is a way around the mask.
 *
 * Names are NOT masked, and that is a decision (Roby, 2026-10-06), not a gap:
 * nothing tells "juan pérez" from "factura abril" reliably, and the words are
 * the point of the event. It is the one exception to "no PII in event
 * properties"; see docs/solutions/patterns/analytics-adapter-posthog.md.
 *
 * An amount written with dots ("1.500.000") reads as a phone number. It is
 * masked either way; only its kind is off.
 *
 * @param term - The trimmed query, as it was sent to the server.
 * @returns The text to emit, lowercased and cut to MASKED_QUERY_MAX_RUNES, and
 *   what it is made of.
 */
export function maskSearchQuery(term: string): {
  masked: string;
  kind: tMessageSearchQueryKind;
} {
  // Cut, fold, cut again. Folding can make a text several times longer (one
  // "㌀" is four letters), and the patterns below take time with the square of
  // the longest word they read: 250 ms for 1,000 of those, measured.
  const head = readablePart(readablePart(term).normalize('NFKC'));

  const words = head
    .trim()
    .toLocaleLowerCase('es')
    .split(/\s+/)
    .filter(Boolean)
    // The whole word, not the span: whatever shares a word with an `@` is part
    // of the address or glued to it.
    .map((word) => (word.includes('@') ? '[email]' : word));

  const masked = words
    .join(' ')
    .replace(LINK_WITH_SCHEME, maskLink)
    .replace(DOMAIN_WITH_PATH, maskLink)
    .replace(DIGIT_RUN, (run) =>
      run.replace(NOT_A_DIGIT, '').length >= PHONE_MIN_DIGITS ? '[phone]' : run,
    )
    .replace(LONG_NUMBER, '[number]');

  return {
    masked: Array.from(masked).slice(0, MASKED_QUERY_MAX_RUNES).join(''),
    // Of the whole query, not of what the cap left of it: a token cut in half
    // would read as a word.
    kind: kindOf(masked),
  };
}

/**
 * An id for one search session. `crypto.randomUUID` only exists in a secure
 * context, and a dev host on plain HTTP is not one: it still gets an id, so
 * analytics is never what breaks the search.
 */
export function newSearchSessionId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  );
}

/**
 * The payload of one settled query. The term goes in and only its mask, its
 * length and its word count come out: no property carries it as typed.
 *
 * @param search.term - The trimmed query the server answered.
 * @param search.resultCount - Results loaded for it when it settled.
 */
export function buildSearchedProps(search: {
  surface: tMessageSearchSurface;
  term: string;
  firstPageCount: number;
  resultCount: number;
  hasMore: boolean;
  settledBy: tMessageSearchSettledBy;
  sessionId: string;
}): tMessageSearchSearchedProps {
  const { masked, kind } = maskSearchQuery(search.term);

  return {
    surface: search.surface,
    query_masked: masked,
    query_kind: kind,
    query_length: countRunes(search.term),
    word_count: search.term.split(/\s+/).filter(Boolean).length,
    has_results: search.resultCount > 0,
    first_page_count: search.firstPageCount,
    has_more: search.hasMore,
    settled_by: search.settledBy,
    search_session_id: search.sessionId,
  };
}
